import React, { useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import {
  CheckCircle2,
  Clock,
  Target,
  ArrowLeft,
  BookOpen,
  ArrowRight,
  ChevronRight,
  Phone,
  School,
  Star,
  ShieldCheck,
  GraduationCap
} from 'lucide-react';

// Data Imports
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/common/PageHeader';
import { fetchCourses } from '@/store/slices/coursesSlice';

const CourseDetailPage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { data, fetchLoading, error } = useSelector((state) => state.courses);

  useEffect(() => {
    // Fetch whenever we don't already have course data (e.g. direct page load / refresh)
    if (!data?.data || data.data.length === 0) {
      dispatch(fetchCourses());
    }
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const apiCourses = data?.data || [];

  /* API SHAPE → UI SHAPE (same mapping as CoursesPage) */
  const coursesData = useMemo(
    () =>
      apiCourses.map((c) => ({
        id: c.id,
        name: c.name,
        class: c.class_name,
        target: c.category,
        description: c.description,
        duration: c.duration,
        features: Array.isArray(c.exam_preparation)
          ? c.exam_preparation
          : (c.exam_preparation || '')
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean),
        icon: <BookOpen className="h-12 w-12 text-primary m-auto" />
      })),
    [apiCourses]
  );

  // 1. Fetch Course Data
  const course = useMemo(
    () => coursesData.find((c) => String(c.id) === String(courseId)),
    [coursesData, courseId]
  );

  // Loading state (while API call is in flight)
  if (fetchLoading && !course) {
    return (
      <div className="h-[60vh] flex flex-col items-center justify-center space-y-4">
        <p className="text-muted-foreground text-lg">Loading course details...</p>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="h-[60vh] flex flex-col items-center justify-center space-y-4">
        <h2 className="text-2xl font-bold">Course not found</h2>
        <Button onClick={() => navigate('/courses')}>Back to All Courses</Button>
      </div>
    );
  }

  // 2. Filter Related Courses
  const relatedCourses = coursesData
    .filter(c => c.id !== course.id && (c.class === course.class))
    .slice(0, 3);

  return (
    <div className="bg-background">
      <PageHeader
        title={course.name}
        subtitle={`${course.class} | ${course.target}`}
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'Courses', path: '/courses' },
          { label: course.name }
        ]}
      />

      <main className="container-custom py-10 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">

          {/* LEFT COLUMN: MAIN CONTENT */}
          <div className="lg:col-span-2 space-y-12">

            {/* A. Program Overview & Stats */}
            <section className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="h-16 w-16 p-2 rounded-2xl bg-primary/10 flex items-center justify-center text-2xl shadow-inner">
                  {course.icon}
                </div>
                <h2 className="text-3xl font-bold tracking-tight">Program Overview</h2>
              </div>
              <p className="text-xl text-muted-foreground leading-relaxed">
                {course.description}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <StatCard icon={<School className="text-blue-500" />} label="Class Level" value={course.class} />
                <StatCard icon={<Clock className="text-emerald-500" />} label="Program Duration" value={course.duration} />
                <StatCard icon={<Target className="text-rose-500" />} label="Main Focus" value={course.target?.split('|')[0]} />
              </div>
            </section>

            {/* B. Why Mantra Prayas? (From Document Data) */}
            <section className="p-8 rounded-3xl bg-secondary/30 border border-secondary/50 relative overflow-hidden">
              <div className="relative z-10 space-y-4">
                <h3 className="text-xl font-bold flex items-center gap-2">
                  <ShieldCheck className="text-primary" /> Why Mantra Prayas?
                </h3>
                <p className="text-muted-foreground italic leading-relaxed">
                  "If we teach today's students as we taught yesterday, we rob them of their tomorrow. At MANTRA PRAYAS, we constantly work on improving and upgrading the teaching methodology to match the latest trends in education."
                </p>
                <div className="flex flex-wrap gap-3 pt-2">
                  <Badge variant="outline" className="bg-background/50">Modern Methodology</Badge>
                  <Badge variant="outline" className="bg-background/50">Interdisciplinary Learning</Badge>
                  <Badge variant="outline" className="bg-background/50">Smart Knowledge </Badge>
                </div>
              </div>
              <div className="absolute -right-10 -bottom-10 opacity-5">
                <Star size={200} fill="currentColor" />
              </div>
            </section>

            {/* C. Program Highlights */}
            <section className="space-y-6">
              <h3 className="text-2xl font-bold flex items-center gap-2">
                <BookOpen className="h-6 w-6 text-primary" />
                What You'll Achieve
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {course.features.map((feature, idx) => (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    viewport={{ once: true }}
                    key={idx}
                    className="flex items-center gap-4 p-5 rounded-2xl border bg-card hover:shadow-md transition-shadow"
                  >
                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <CheckCircle2 className="h-5 w-5 text-primary" />
                    </div>
                    <span className="text-sm font-semibold">{feature}</span>
                  </motion.div>
                ))}
              </div>
            </section>

            {/* D. Our Mission (Trust Builder) */}
            <section className="border-l-4 border-primary pl-6 py-2">
              <h3 className="text-lg font-bold text-primary uppercase tracking-widest mb-2">Our Mission</h3>
              <p className="text-muted-foreground leading-relaxed">
                To enable young aspirants to achieve success in Olympiads, IIT-JEE, and NEET through dedicated faculty members and focused study centers.
              </p>
            </section>
          </div>

          {/* RIGHT COLUMN: STICKY ENROLLMENT CARD */}
          <div className="lg:col-span-1">
            <aside className="sticky top-24 space-y-6">
              <Card className="overflow-hidden shadow-2xl border-none ring-1 ring-border">
                <div className="bg-primary p-6 text-primary-foreground text-center">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] opacity-80 mb-1">Session 2025-26</p>
                  <h4 className="text-2xl font-bold">Admission Open</h4>
                </div>
                <CardContent className="p-8 space-y-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Class:</span>
                      <span className="font-bold">{course.class}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Location:</span>
                      <span className="font-bold">Bhilwara Campus</span>
                    </div>
                  </div>

                  <div className="space-y-3 pt-4">
                    <a href="/enquiry" className="block">
                      <Button className="w-full h-14 text-lg font-bold group" size="lg">
                        Enquire Now
                        <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </a>
                    <a href="tel:+918839354369" className="block">
                      <Button variant="outline" size="lg" className="w-full h-14 text-lg font-bold border-2">
                        <Phone className="mr-2 h-5 w-5" />
                        +91 88393 54369
                      </Button>
                    </a>
                  </div>

                  <p className="text-[10px] text-center text-muted-foreground uppercase tracking-wider">
                    Registration office: Sindhu Nagar, Bhilwara
                  </p>
                </CardContent>
              </Card>

              {/* Founder Quote */}
              <div className="p-6 rounded-2xl bg-muted/50 border border-dashed text-center">
                <GraduationCap className="mx-auto mb-3 text-primary h-8 w-8" />
                <p className="text-xs italic text-muted-foreground mb-4">
                  "No aim is too big and no case is too trivial when it comes to aspiring for the dream."
                </p>
                <p className="text-sm font-bold">— Piyush Soni Sir </p>
              </div>
            </aside>
          </div>
        </div>

        {/* RELATED COURSES SECTION */}
        {relatedCourses.length > 0 && (
          <section className="mt-24 pt-16 border-t">
            <div className="flex items-end justify-between mb-10">
              <div>
                <h3 className="text-3xl font-bold">Similar Programs</h3>
                <p className="text-muted-foreground">Other options for students in {course.class}</p>
              </div>
              <Link to="/courses" className="text-primary font-bold hover:underline flex items-center gap-1">
                View All <ChevronRight size={16} />
              </Link>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {relatedCourses.map((rc) => (
                <Link key={rc.id} to={`/courses/${rc.id}`} className="group">
                  <Card className="h-full border-none shadow-sm group-hover:shadow-xl group-hover:-translate-y-2 transition-all duration-300 ring-1 ring-border">
                    <CardContent className="p-6">
                      <div className="h-10 w-10 rounded-lg bg-primary/5 flex items-center justify-center text-xl mb-4">
                        {rc.icon}
                      </div>
                      <h4 className="font-bold text-lg mb-2 group-hover:text-primary transition-colors">{rc.name}</h4>
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-4">{rc.description}</p>
                      <div className="flex items-center text-xs font-bold text-primary">
                        EXPLORE PROGRAM <ArrowRight className="h-3 w-3 ml-2 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

// --- Sub-components ---

const StatCard = ({ icon, label, value }) => (
  <div className="flex items-center gap-4 p-5 rounded-2xl border bg-card shadow-sm hover:border-primary/50 transition-colors">
    <div className="p-3 bg-muted rounded-xl">
      {React.cloneElement(icon, { size: 24 })}
    </div>
    <div>
      <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-bold mb-0.5">{label}</p>
      <p className="text-sm font-extrabold">{value}</p>
    </div>
  </div>
);

export default CourseDetailPage;